import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  CalendarDays,
  CalendarRange,
  ChevronRight,
  Plus,
  Sparkles,
  Trash2,
} from 'lucide-react'
import { differenceInCalendarDays, format, parseISO } from 'date-fns'
import RangeCalendarPicker from './RangeCalendarPicker'
import {
  CourseDateRangeRow,
  createEmptyDateRangeRow,
  formatDateRangeLabel,
  completedRangesFromRows,
  todayISODate,
} from '../utils/courseSchedule'

interface CourseDateRangesEditorProps {
  rows: CourseDateRangeRow[]
  onChange: (rows: CourseDateRangeRow[]) => void
  minDate?: string
}

function formatSingleDate(iso: string): string {
  return format(parseISO(iso), 'd MMM yyyy')
}

function rangeDurationLabel(startDate: string, endDate: string): string | null {
  if (!startDate || !endDate || endDate < startDate) return null
  const days = differenceInCalendarDays(parseISO(endDate), parseISO(startDate)) + 1
  return days === 1 ? '1 day' : `${days} days`
}

export default function CourseDateRangesEditor({
  rows,
  onChange,
  minDate = todayISODate(),
}: CourseDateRangesEditorProps) {
  const [activeId, setActiveId] = useState<string>(() => rows[0]?.id ?? '')
  const batchListRef = useRef<HTMLDivElement>(null)
  const batchItemRefs = useRef<Record<string, HTMLDivElement | null>>({})

  useEffect(() => {
    if (rows.length && !rows.some((r) => r.id === activeId)) {
      setActiveId(rows[0].id)
    }
  }, [rows, activeId])

  const scrollActiveBatchIntoView = useCallback((id: string) => {
    requestAnimationFrame(() => {
      const el = batchItemRefs.current[id]
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        return
      }
      const list = batchListRef.current
      if (list) {
        list.scrollTo({ top: list.scrollHeight, behavior: 'smooth' })
      }
    })
  }, [])

  useEffect(() => {
    if (activeId) scrollActiveBatchIntoView(activeId)
  }, [activeId, scrollActiveBatchIntoView])

  const activeRow = rows.find((r) => r.id === activeId) ?? rows[0]
  const completedRanges = useMemo(() => completedRangesFromRows(rows), [rows])

  const updateActiveRange = (startDate: string, endDate: string) => {
    if (!activeRow) return
    onChange(
      rows.map((r) =>
        r.id === activeRow.id ? { ...r, startDate, endDate } : r,
      ),
    )
    scrollActiveBatchIntoView(activeRow.id)
  }

  const removeRow = (id: string) => {
    const next = rows.filter((r) => r.id !== id)
    const fallback = next.length ? next : [createEmptyDateRangeRow()]
    onChange(fallback)
    if (activeId === id) {
      setActiveId(fallback[0].id)
    }
  }

  const addRow = () => {
    const row = createEmptyDateRangeRow()
    onChange([...rows, row])
    setActiveId(row.id)
    scrollActiveBatchIntoView(row.id)
  }

  if (!activeRow) {
    return null
  }

  return (
    <div className="rounded-2xl border border-primary-200/50 bg-gradient-to-br from-white via-white to-primary-50/40 shadow-xl shadow-primary-900/[0.06] overflow-hidden">
      {/* Header */}
      <div className="relative px-6 py-5 border-b border-primary-100/80 bg-gradient-to-r from-primary-500/10 via-primary-600/5 to-transparent">
        <div className="absolute top-0 right-0 w-48 h-48 bg-primary-400/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4 pointer-events-none" />
        <div className="relative flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 text-white shadow-lg shadow-primary-600/25">
            <CalendarRange className="h-6 w-6" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 tracking-tight">
              Course batches & dates
            </h3>
            <p className="text-sm text-gray-600 mt-1 max-w-xl leading-relaxed">
              Add multiple start and end dates on the calendar. Batches can overlap — for example
              13 May–10 Jun and 1 Jun–26 Jun.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,340px)_1fr] gap-0 lg:divide-x divide-primary-100/80">
        {/* Batch list */}
        <div className="p-5 lg:p-6 bg-gray-50/40 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 px-1">
            Your batches
          </p>
          <div
            ref={batchListRef}
            className="space-y-2.5 max-h-[420px] overflow-y-auto pr-1 custom-scrollbar"
          >
            <AnimatePresence initial={false}>
              {rows.map((row, index) => {
                const isActive = row.id === activeRow.id
                const complete = row.startDate && row.endDate && row.endDate >= row.startDate
                const duration = complete
                  ? rangeDurationLabel(row.startDate, row.endDate)
                  : null

                return (
                  <motion.div
                    key={row.id}
                    ref={(el) => {
                      batchItemRefs.current[row.id] = el
                    }}
                    role="button"
                    tabIndex={0}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    onClick={() => setActiveId(row.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setActiveId(row.id)
                      }
                    }}
                    className={[
                      'w-full text-left rounded-xl border-2 p-4 transition-all duration-200 cursor-pointer',
                      isActive
                        ? 'border-primary-500 bg-white shadow-md shadow-primary-600/10 ring-1 ring-primary-500/20'
                        : 'border-gray-200/90 bg-white/80 hover:border-primary-300 hover:shadow-sm',
                    ].join(' ')}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className={[
                            'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold',
                            isActive
                              ? 'bg-primary-600 text-white'
                              : 'bg-gray-100 text-gray-600',
                          ].join(' ')}
                        >
                          {index + 1}
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            Batch {index + 1}
                          </p>
                          {complete ? (
                            <p className="text-xs text-gray-600 mt-0.5 leading-snug">
                              {formatDateRangeLabel(row.startDate, row.endDate)}
                            </p>
                          ) : (
                            <p className="text-xs text-amber-700 mt-0.5">Tap calendar to set dates</p>
                          )}
                        </div>
                      </div>
                      {isActive && (
                        <ChevronRight className="h-4 w-4 text-primary-600 shrink-0 mt-1" />
                      )}
                    </div>
                    {duration && (
                      <span className="inline-flex mt-2.5 items-center gap-1 rounded-full bg-primary-50 px-2.5 py-0.5 text-[11px] font-medium text-primary-800 border border-primary-100">
                        <Sparkles className="h-3 w-3" />
                        {duration}
                      </span>
                    )}
                    {rows.length > 1 && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          removeRow(row.id)
                        }}
                        className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Remove batch
                      </button>
                    )}
                  </motion.div>
                )
              })}
            </AnimatePresence>
          </div>

          <button
            type="button"
            onClick={addRow}
            className="w-full flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-primary-300/80 bg-white/60 px-4 py-3.5 text-sm font-semibold text-primary-700 transition hover:border-primary-500 hover:bg-primary-50/80"
          >
            <Plus className="h-4 w-4" />
            Add another batch
          </button>
        </div>

        {/* Calendar panel */}
        <div className="p-5 lg:p-8 bg-white">
          <div className="flex items-center gap-2 mb-6">
            <CalendarDays className="h-5 w-5 text-primary-600" />
            <h4 className="text-sm font-semibold text-gray-900">
              Editing batch {rows.findIndex((r) => r.id === activeRow.id) + 1}
            </h4>
          </div>

          <div className="rounded-2xl border border-gray-100 bg-gradient-to-b from-gray-50/50 to-white p-5 sm:p-6 shadow-inner shadow-gray-900/[0.02]">
            <RangeCalendarPicker
              key={activeRow.id}
              startDate={activeRow.startDate}
              endDate={activeRow.endDate}
              minDate={minDate}
              onChange={updateActiveRange}
            />
          </div>

          {/* Quick readout */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-xl border border-gray-100 bg-gray-50/80 px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">
                Start
              </p>
              <p className="text-sm font-semibold text-gray-900">
                {activeRow.startDate ? formatSingleDate(activeRow.startDate) : '—'}
              </p>
            </div>
            <div className="rounded-xl border border-gray-100 bg-gray-50/80 px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">
                End
              </p>
              <p className="text-sm font-semibold text-gray-900">
                {activeRow.endDate ? formatSingleDate(activeRow.endDate) : '—'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer summary */}
      {completedRanges.length > 0 && (
        <div className="px-6 py-4 bg-gradient-to-r from-primary-600/5 to-primary-500/10 border-t border-primary-100/80">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary-800/70 mb-2">
            Scheduled batches
          </p>
          <div className="flex flex-wrap gap-2">
            {completedRanges.map((r, i) => (
              <span
                key={`${r.startDate}-${r.endDate}-${i}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-white/90 border border-primary-200/80 px-3 py-1.5 text-xs font-medium text-gray-800 shadow-sm"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
                {formatDateRangeLabel(r.startDate, r.endDate)}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
